import { EventType, IncrementalSource, MouseInteractionType } from './events.js';
const TEXT_CAP = 60;
const MAX_STEPS = 200;
/** The recorded value is a privacy mask, not real input. */
const MASKED_RE = /^[•*]+$/;
function collectText(n) {
    let out = '';
    const walk = (x) => {
        if (out.length > TEXT_CAP)
            return;
        if (x.type === 3 && x.textContent)
            out += x.textContent;
        for (const c of x.childNodes ?? [])
            walk(c);
    };
    walk(n);
    return out.replace(/\s+/g, ' ').trim().slice(0, TEXT_CAP);
}
/** Indexes every element in a snapshot subtree (id → tag/attrs/parent/text). */
function indexTree(root, parentId, into) {
    if (root.type === 2 && root.tagName) {
        into.set(root.id, {
            tag: root.tagName.toLowerCase(),
            attrs: root.attributes ?? {},
            parentId,
            text: collectText(root),
        });
    }
    for (const c of root.childNodes ?? []) {
        indexTree(c, root.type === 2 ? root.id : parentId, into);
    }
}
const cssSafe = (s) => /^[a-zA-Z][a-zA-Z0-9_-]*$/.test(s);
/** Selector candidates for a node, most specific first. */
function selectorsFor(id, nodes) {
    const n = nodes.get(id);
    if (!n)
        return [];
    const out = [];
    for (const key of ['data-testid', 'data-test', 'data-cy']) {
        const v = n.attrs[key];
        if (v)
            out.push(`[${key}="${v}"]`);
    }
    if (n.attrs.id && cssSafe(n.attrs.id))
        out.push(`#${n.attrs.id}`);
    if (n.attrs.name && cssSafe(n.attrs.name))
        out.push(`${n.tag}[name="${n.attrs.name}"]`);
    const classes = (n.attrs.class ?? '').split(/\s+/).filter(cssSafe).slice(0, 3);
    const tagClass = n.tag + classes.map((c) => `.${c}`).join('');
    // Anchor to one ancestor with classes when it adds specificity.
    const parent = n.parentId != null ? nodes.get(n.parentId) : undefined;
    if (parent) {
        const pClasses = (parent.attrs.class ?? '').split(/\s+/).filter(cssSafe).slice(0, 2);
        if (pClasses.length || parent.tag !== 'div') {
            out.push(`${parent.tag}${pClasses.map((c) => `.${c}`).join('')} ${tagClass}`);
        }
    }
    out.push(tagClass);
    return [...new Set(out)];
}
/** Human name for a node: its text, else a descriptive attribute, else the tag. */
function labelFor(id, nodes) {
    const n = nodes.get(id);
    if (!n)
        return 'element';
    if (n.text)
        return `"${n.text}"`;
    const desc = n.attrs['aria-label'] ?? n.attrs.placeholder ?? n.attrs.title ?? n.attrs.alt ?? n.attrs.name ?? n.attrs.id;
    if (desc)
        return `"${desc}"`;
    return `<${n.tag}>`;
}
/** Field descriptor for input steps, e.g. `Email` or `input[name="card"]`. */
function fieldFor(id, nodes) {
    const n = nodes.get(id);
    if (!n)
        return 'field';
    const desc = n.attrs.placeholder ?? n.attrs['aria-label'] ?? n.attrs.name ?? n.attrs.id;
    return desc ? `"${desc}"` : `<${n.tag}>`;
}
/** Attributes worth carrying on a step: they identify the element and survive text masking. */
const CARRIED_ATTRS = ['data-testid', 'data-test', 'data-cy', 'aria-label', 'placeholder', 'name', 'type', 'title', 'href'];
/** Tag, role and identifying attributes of a node, for consumers that inventory elements. */
function targetOf(id, nodes) {
    const n = nodes.get(id);
    if (!n)
        return {};
    const attrs = {};
    for (const key of CARRIED_ATTRS) {
        const v = n.attrs[key];
        if (typeof v === 'string' && v)
            attrs[key] = v.slice(0, 200);
    }
    return {
        tag: n.tag,
        role: n.attrs.role || undefined,
        attrs: Object.keys(attrs).length ? attrs : undefined,
    };
}
const INTERACTIVE_TAGS = new Set(['button', 'a', 'input', 'select', 'textarea']);
const INTERACTIVE_ROLES = new Set(['button', 'link', 'tab', 'menuitem', 'checkbox', 'radio', 'switch', 'combobox']);
function isInteractive(n) {
    if (n.tag === 'a')
        return typeof n.attrs.href === 'string';
    if (n.tag === 'input')
        return n.attrs.type !== 'hidden';
    if (INTERACTIVE_TAGS.has(n.tag))
        return true;
    if (n.attrs.role && INTERACTIVE_ROLES.has(n.attrs.role))
        return true;
    return 'data-testid' in n.attrs;
}
/**
 * Every interactive element the recording's DOM contained, with the page it
 * appeared on. The complement of compileSteps: steps say what was used, this
 * says what was there — and a coverage tool needs both, because the finding
 * is the button nobody pressed. Elements are reported once per appearance
 * (a snapshot or a mutation that added them); the consumer dedupes by
 * selector within a page.
 */
export function compileInventory(events) {
    const sorted = [...events].sort((a, b) => a.timestamp - b.timestamp);
    const nodes = new Map();
    const out = [];
    let url = null;
    const emit = (fresh) => {
        for (const [id, n] of fresh) {
            nodes.set(id, n);
            if (!isInteractive(n))
                continue;
            out.push({
                url,
                selectors: selectorsFor(id, nodes),
                text: n.text || undefined,
                ...targetOf(id, nodes),
            });
        }
    };
    for (const e of sorted) {
        if (e.type === EventType.Meta) {
            const href = e.data.href;
            if (href)
                url = href;
        }
        else if (e.type === EventType.FullSnapshot) {
            const node = e.data.node;
            if (!node)
                continue;
            // A new snapshot is a new document: the old ids mean nothing now.
            nodes.clear();
            const fresh = new Map();
            indexTree(node, undefined, fresh);
            emit(fresh);
        }
        else if (e.type === EventType.IncrementalSnapshot) {
            const d = e.data;
            if (d.source !== IncrementalSource.Mutation)
                continue;
            for (const add of d.adds ?? []) {
                if (!add.node)
                    continue;
                if (add.node.type === 3 && add.node.textContent && typeof add.parentId === 'number') {
                    const p = nodes.get(add.parentId);
                    if (p)
                        p.text = `${p.text} ${add.node.textContent}`.replace(/\s+/g, ' ').trim().slice(0, TEXT_CAP);
                    continue;
                }
                const fresh = new Map();
                indexTree(add.node, add.parentId, fresh);
                emit(fresh);
            }
        }
    }
    return out;
}
export function compileSteps(events, options = {}) {
    const maxSteps = options.maxSteps ?? MAX_STEPS;
    const sorted = [...events].sort((a, b) => a.timestamp - b.timestamp);
    const start = sorted.length ? sorted[0].timestamp : 0;
    const nodes = new Map();
    const steps = [];
    let opened = false;
    // Consecutive keystrokes in one field arrive as many Input events; only the
    // final value is a step. Track the last input per node and flush lazily.
    const pendingInput = new Map();
    const flushInputs = () => {
        for (const s of pendingInput.values())
            steps.push(s);
        pendingInput.clear();
    };
    for (const e of sorted) {
        const offsetMs = e.timestamp - start;
        if (e.type === EventType.FullSnapshot) {
            const node = e.data.node;
            if (node)
                indexTree(node, undefined, nodes);
        }
        else if (e.type === EventType.Meta) {
            const href = e.data.href;
            // A periodic re-snapshot re-announces the same URL — that's not a step.
            const lastNav = [...steps].reverse().find((s) => s.url);
            if (href && href !== lastNav?.url) {
                flushInputs();
                steps.push({
                    offsetMs,
                    kind: opened ? 'navigate' : 'open',
                    label: `${opened ? 'Navigate to' : 'Open'} ${href}`,
                    url: href,
                });
                opened = true;
            }
        }
        else if (e.type === EventType.IncrementalSnapshot) {
            const d = e.data;
            if (d.source === IncrementalSource.Mutation) {
                for (const add of d.adds ?? []) {
                    if (!add.node)
                        continue;
                    // rrweb mutations add nodes one by one: an element's text arrives as
                    // a separate text-node add pointing at the parent — fold it in, or
                    // every dynamically-added button would be labelled bare `<button>`.
                    if (add.node.type === 3 && add.node.textContent && typeof add.parentId === 'number') {
                        const p = nodes.get(add.parentId);
                        if (p)
                            p.text = `${p.text} ${add.node.textContent}`.replace(/\s+/g, ' ').trim().slice(0, TEXT_CAP);
                    }
                    else {
                        indexTree(add.node, add.parentId, nodes);
                    }
                }
            }
            else if (d.source === IncrementalSource.MouseInteraction &&
                (d.type === MouseInteractionType.Click || d.type === MouseInteractionType.DblClick) &&
                typeof d.id === 'number') {
                flushInputs();
                steps.push({
                    offsetMs,
                    kind: 'click',
                    label: `${d.type === MouseInteractionType.DblClick ? 'Double-click' : 'Click'} ${labelFor(d.id, nodes)}`,
                    selectors: selectorsFor(d.id, nodes),
                    text: nodes.get(d.id)?.text || undefined,
                    x: d.x,
                    y: d.y,
                    ...targetOf(d.id, nodes),
                });
            }
            else if (d.source === IncrementalSource.Input && typeof d.id === 'number' && typeof d.text === 'string') {
                const masked = MASKED_RE.test(d.text);
                const node = nodes.get(d.id);
                pendingInput.set(d.id, {
                    offsetMs,
                    kind: 'input',
                    label: `Type ${masked ? 'into' : `${JSON.stringify(d.text.slice(0, 30))} into`} ${fieldFor(d.id, nodes)}`,
                    selectors: selectorsFor(d.id, nodes),
                    value: d.text.slice(0, 200),
                    masked: masked || undefined,
                    inputType: node?.attrs.type,
                    ...targetOf(d.id, nodes),
                });
            }
        }
    }
    flushInputs();
    // Steps within one moment can land out of order via the lazy input flush.
    steps.sort((a, b) => a.offsetMs - b.offsetMs);
    return steps.slice(0, maxSteps);
}
//# sourceMappingURL=steps.js.map